import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { formatCurrency, formatDate, formatCommissionRate } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';
import { useEntity } from '../../context/EntityContext';
import { isAdminLikeRole } from '../../lib/roles';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { toast } from 'sonner';
import {
  DollarSign, Check, Clock, CheckCircle2, Eye, FileText, Wallet, AlertCircle, Search,
} from 'lucide-react';
import { claimAPI } from '../../lib/api';
import { statusColors, statusLabels } from './claims/claimConstants';
import { ClaimDetailsSheet } from './claims/ClaimDetailsSheet';
import { PaymentTrackerDialog } from './claims/PaymentTrackerDialog';
import { RevisionDialog, AuditTrailDialog } from './claims/ClaimDialogs';

const AdminCommissionClaims = () => {
  const { user } = useAuth();
  const { currentEntity, switchEntity, config: entityConfig } = useEntity();
  const [searchParams] = useSearchParams();
  const agentIdFilter = searchParams.get('agent_id');
  const [agentInfo, setAgentInfo] = useState(null);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedClaim, setSelectedClaim] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [revisionDialogOpen, setRevisionDialogOpen] = useState(false);
  const [revisionComment, setRevisionComment] = useState('');
  const [fieldComments, setFieldComments] = useState({});
  const [auditTrailOpen, setAuditTrailOpen] = useState(false);
  const [auditData, setAuditData] = useState(null);
  const [trackerOpen, setTrackerOpen] = useState(false);
  const [trackerData, setTrackerData] = useState(null);
  const [trackerLoading, setTrackerLoading] = useState(false);
  const [devFundOpen, setDevFundOpen] = useState(false);
  const [devFundForm, setDevFundForm] = useState({ payment_date: '', payment_ref: '', payment_amount: '' });
  const [payRecipientOpen, setPayRecipientOpen] = useState(null);
  const [payForm, setPayForm] = useState({ payment_method: 'bank_transfer', payment_ref: '', payment_date: '' });
  const [payLoading, setPayLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  // Pagination + year filter so 1k+ claims (2024–2026) are all reachable
  const [yearFilter, setYearFilter] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);
  const [totalClaims, setTotalClaims] = useState(0);

  const isPIC = user?.role === 'pic';
  const isAccount = user?.role === 'account';
  const isAdmin = isAdminLikeRole(user?.role);
  const canApprove = isPIC || isAdmin || isAccount;

  useEffect(() => { loadClaims(); loadDashboardStats(); if (agentIdFilter) loadAgentInfo(); }, [agentIdFilter, currentEntity, yearFilter, searchText, page]);

  const loadAgentInfo = async () => { try { const res = await api.get(`/users/${agentIdFilter}`); setAgentInfo(res.data); } catch {} };
  const loadClaims = async () => {
    try {
      setLoading(true);
      let url = agentIdFilter ? `/agents/${agentIdFilter}/commission-claims` : '/commission-claims';
      const params = {};
      if (currentEntity !== 'all') params.entity = currentEntity;
      if (!agentIdFilter) {
        if (yearFilter && yearFilter !== 'all') params.year = yearFilter;
        if (searchText.trim()) params.q = searchText.trim();
        params.page = page;
        params.limit = pageSize;
      }
      const res = await api.get(url, { params });
      // Paginated dict {items,total,...} OR plain array (per-agent endpoint)
      if (Array.isArray(res.data)) {
        setClaims(res.data);
        setTotalClaims(res.data.length);
      } else {
        setClaims(res.data?.items || []);
        setTotalClaims(res.data?.total ?? 0);
      }
    } catch { toast.error('Failed to load claims'); } finally { setLoading(false); }
  };
  const loadDashboardStats = async () => {
    try { const entityParam = currentEntity !== 'all' ? `?entity=${currentEntity}` : ''; const res = await api.get(`/commission-claims/dashboard/stats${entityParam}`); setDashboardStats(res.data); } catch {}
  };
  const loadClaimDetails = async (claimId) => { try { const res = await api.get(`/commission-claims/${claimId}`); setSelectedClaim(res.data); setDetailsOpen(true); } catch { toast.error('Failed to load claim details'); } };
  const loadAuditTrail = async (claimId) => { try { const res = await api.get(`/commission-claims/${claimId}/audit-trail`); setAuditData(res.data); setAuditTrailOpen(true); } catch { toast.error('Failed to load audit trail'); } };

  const openPaymentTracker = async (claim) => {
    setTrackerLoading(true); setTrackerOpen(true);
    try { const res = await claimAPI.getPaymentTracker(claim.id); setTrackerData(res.data); } catch { toast.error('Failed to load payment tracker'); } finally { setTrackerLoading(false); }
  };
  const handleMarkDeveloperFunded = async () => {
    if (!trackerData?.claim_id) return; setPayLoading(true);
    try { await claimAPI.markDeveloperFunded(trackerData.claim_id, devFundForm); toast.success('Developer payment recorded'); setDevFundOpen(false); const res = await claimAPI.getPaymentTracker(trackerData.claim_id); setTrackerData(res.data); loadClaims(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setPayLoading(false); }
  };
  const handleMarkRecipientPaid = async () => {
    if (!trackerData?.claim_id || payRecipientOpen === null) return; setPayLoading(true);
    try { await claimAPI.markRecipientPaid(trackerData.claim_id, payRecipientOpen, payForm); toast.success('Payment recorded & receipt generated'); setPayRecipientOpen(null); const res = await claimAPI.getPaymentTracker(trackerData.claim_id); setTrackerData(res.data); loadClaims(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setPayLoading(false); }
  };
  const handleDownloadReceipt = async (claimId, recipientIdx) => {
    try {
      const res = await claimAPI.downloadReceipt(claimId, recipientIdx);
      const { pdf_base64, filename } = res.data;
      const byteChars = atob(pdf_base64);
      const byteNums = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) byteNums[i] = byteChars.charCodeAt(i);
      const blob = new Blob([new Uint8Array(byteNums)], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
      toast.success('Receipt downloaded');
    } catch { toast.error('Receipt not available'); }
  };

  const handleStartReview = async (claimId) => {
    if (!canApprove) return;
    try { await api.post(`/commission-claims/${claimId}/start-review`); toast.success('Review started'); loadClaims(); if (selectedClaim?.id === claimId) loadClaimDetails(claimId); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start review'); }
  };
  const handleApprove = async (claimId) => {
    if (!canApprove) return;
    try { const res = await api.post(`/commission-claims/${claimId}/approve`); toast.success(res.data?.message || 'Claim approved'); loadClaims(); if (selectedClaim?.id === claimId) loadClaimDetails(claimId); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to approve claim'); }
  };
  const handleProcessPayment = async (claimId) => {
    if (!isAccount) return;
    try { const res = await api.post(`/commission-claims/${claimId}/process-payment`); toast.success(`Payment processed! ${res.data.payments} payments totaling ${formatCurrency(res.data.total_paid)}`); loadClaims(); if (selectedClaim?.id === claimId) loadClaimDetails(claimId); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to process payment'); }
  };
  const handleRequestRevision = async () => {
    if (!selectedClaim || !canApprove) return;
    try { await api.post(`/commission-claims/${selectedClaim.id}/request-revision`, { field_comments: fieldComments, general_comment: revisionComment }); toast.success('Revision requested'); setRevisionDialogOpen(false); setRevisionComment(''); setFieldComments({}); loadClaims(); loadClaimDetails(selectedClaim.id); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleReject = async (claimId, reason) => {
    if (!canApprove) return;
    try { await api.post(`/commission-claims/${claimId}/reject?reason=${encodeURIComponent(reason)}`); toast.success('Claim rejected'); loadClaims(); setDetailsOpen(false); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const filteredClaims = claims.filter(c => {
    if (activeTab === 'all') return true;
    if (activeTab === 'pending') return ['submitted', 'under_review'].includes(c.status);
    return c.status === activeTab;
  });
  const pendingCount = claims.filter(c => ['submitted', 'under_review'].includes(c.status)).length;

  if (loading) return <div className="flex items-center justify-center min-h-[400px]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="admin-commission-claims">
      {/* Header */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
            {agentIdFilter && agentInfo ? `Claims - ${agentInfo.full_name}` : 'Claims Management'}
          </h1>
          <p className="text-sm text-gray-500">{agentIdFilter && agentInfo ? `Viewing commission claims for ${agentInfo.agent_code || agentInfo.full_name}` : 'Review and process commission claims'}</p>
        </div>
        {pendingCount > 0 && <Badge variant="destructive" className="text-sm px-3 py-1 self-start sm:self-auto">{pendingCount} Pending Review</Badge>}
      </div>

      {/* Entity filter banner — so admins/PIC/account never miss claims that are hidden
          because the domain context pre-selected a single entity. */}
      {currentEntity !== 'all' && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="entity-filter-banner">
          <AlertCircle className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 text-sm text-amber-800">
            <span className="font-medium">Showing {entityConfig[currentEntity]?.label} claims only.</span>{' '}
            Claims from the other entity are hidden — click the button to see all entities.
          </div>
          <Button size="sm" variant="outline" className="border-amber-400 text-amber-800 hover:bg-amber-100" onClick={() => switchEntity('all')} data-testid="show-all-entities-btn">
            Show all entities
          </Button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
        {[
          { icon: FileText, color: 'bg-blue-100 text-blue-600', label: 'Total Claims', value: dashboardStats?.total_claims || claims.length },
          { icon: Clock, color: 'bg-yellow-100 text-yellow-600', label: isPIC ? 'Awaiting Your Review' : isAccount ? 'Awaiting Your Action' : 'Pending', value: dashboardStats?.pending_for_you || pendingCount, highlight: (dashboardStats?.pending_for_you || 0) > 0 },
          { icon: CheckCircle2, color: 'bg-cyan-100 text-cyan-600', label: 'PIC Approved', value: dashboardStats?.by_status?.pic_approved?.count || claims.filter(c => c.status === 'pic_approved').length },
          { icon: CheckCircle2, color: 'bg-green-100 text-green-600', label: 'Fully Approved', value: dashboardStats?.by_status?.approved?.count || claims.filter(c => c.status === 'approved').length },
          { icon: DollarSign, color: 'bg-emerald-100 text-emerald-600', label: 'Total Paid', value: formatCurrency(dashboardStats?.total_paid_amount || 0), isCurrency: true },
        ].map((s, i) => (
          <Card key={i} className={s.highlight ? 'border-yellow-300 bg-yellow-50' : ''}>
            <CardContent className="p-3 sm:p-4">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className={`p-2 ${s.color.split(' ')[0]} rounded-lg shrink-0`}><s.icon className={`h-5 w-5 ${s.color.split(' ')[1]}`} /></div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm text-gray-500 truncate">{s.label}</p>
                  <p className={`text-lg sm:text-xl font-bold truncate ${s.highlight ? 'text-yellow-600' : s.isCurrency ? 'text-emerald-600' : ''}`}>{s.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Claims Table */}
      <Card>
        <div className="p-0">
          {/* Filters: year + search (only on global list, not per-agent) */}
          {!agentIdFilter && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 px-3 sm:px-4 py-3 border-b bg-gray-50">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase shrink-0">Year</span>
                <Select value={yearFilter} onValueChange={(v) => { setYearFilter(v); setPage(1); }}>
                  <SelectTrigger className="w-32" data-testid="claim-year-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    <SelectItem value="2026">2026</SelectItem>
                    <SelectItem value="2025">2025</SelectItem>
                    <SelectItem value="2024">2024</SelectItem>
                    <SelectItem value="2023">2023</SelectItem>
                    <SelectItem value="2022">2022</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <form onSubmit={(e) => { e.preventDefault(); setSearchText(searchInput); setPage(1); }} className="relative flex-1 sm:max-w-sm">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search ref, agent, project, unit..."
                  className="pl-9"
                  data-testid="claim-search-input"
                />
              </form>
              <div className="text-xs sm:text-sm text-gray-500 sm:ml-auto" data-testid="claim-total-count">
                Showing {claims.length} of <span className="font-semibold text-gray-900">{totalClaims}</span> claims
              </div>
            </div>
          )}
          <div className="border-b px-2 sm:px-4 overflow-x-auto">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="h-12 bg-transparent w-max">
                <TabsTrigger value="pending">Pending {pendingCount > 0 && `(${pendingCount})`}</TabsTrigger>
                <TabsTrigger value="pic_approved">PIC Approved</TabsTrigger>
                <TabsTrigger value="admin_approved">Admin Approved</TabsTrigger>
                <TabsTrigger value="approved">Fully Approved</TabsTrigger>
                <TabsTrigger value="paid">Paid</TabsTrigger>
                <TabsTrigger value="revision_requested">Revision</TabsTrigger>
                <TabsTrigger value="rejected">Rejected</TabsTrigger>
                <TabsTrigger value="all">All</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ref</TableHead><TableHead>Entity</TableHead><TableHead>Agent</TableHead>
                <TableHead>Project</TableHead><TableHead>Unit</TableHead><TableHead>SPA Price</TableHead>
                <TableHead>Commission</TableHead><TableHead>Co-Broke</TableHead><TableHead>Status</TableHead>
                <TableHead>Date</TableHead><TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClaims.length === 0 ? (
                <TableRow><TableCell colSpan={11} className="text-center py-8 text-gray-500">No claims found</TableCell></TableRow>
              ) : filteredClaims.map(claim => (
                <TableRow key={claim.id} data-testid={`claim-row-${claim.id}`}>
                  <TableCell className="font-mono text-sm">{claim.claim_ref}</TableCell>
                  <TableCell><Badge className={claim.entity === 'sp_sam' ? 'bg-blue-500 text-white' : 'bg-emerald-500 text-white'}>{claim.entity === 'sp_sam' ? 'SP SAM' : 'SAM Rumah'}</Badge></TableCell>
                  <TableCell><div><p className="font-medium">{claim.agent_name}</p><p className="text-xs text-gray-500">{claim.agent_code}</p></div></TableCell>
                  <TableCell>{claim.project_name}</TableCell>
                  <TableCell>{claim.unit_number}</TableCell>
                  <TableCell>{formatCurrency(claim.spa_price)}</TableCell>
                  <TableCell><div><p className="font-medium">{formatCurrency(claim.total_commission)}</p><p className="text-xs text-gray-500">{formatCommissionRate(claim)}</p></div></TableCell>
                  <TableCell>{claim.is_cobroke ? <Badge variant="outline" className="bg-purple-50 text-purple-700">Yes</Badge> : <span className="text-gray-400">No</span>}</TableCell>
                  <TableCell><Badge className={`${statusColors[claim.status]} text-white`}>{statusLabels[claim.status]}</Badge></TableCell>
                  <TableCell className="text-sm text-gray-500">{formatDate(claim.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => loadClaimDetails(claim.id)} data-testid={`view-claim-${claim.id}`}><Eye className="h-4 w-4" /></Button>
                      {claim.status === 'submitted' && isPIC && <Button size="sm" variant="outline" onClick={() => handleStartReview(claim.id)}>Start Review</Button>}
                      {['submitted', 'under_review'].includes(claim.status) && isPIC && <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => handleApprove(claim.id)}><Check className="h-4 w-4 mr-1" /> PIC Approve</Button>}
                      {claim.status === 'pic_approved' && isAdmin && <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => handleApprove(claim.id)}><Check className="h-4 w-4 mr-1" /> Admin Approve</Button>}
                      {claim.status === 'admin_approved' && isAccount && <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(claim.id)}><Check className="h-4 w-4 mr-1" /> Account Approve</Button>}
                      {['admin_approved', 'approved', 'approved_pending_fund', 'developer_funded', 'partially_paid', 'fully_paid', 'paid'].includes(claim.status) && (
                        <Button size="sm" className="bg-teal-600 hover:bg-teal-700" onClick={() => openPaymentTracker(claim)} data-testid={`payment-tracker-btn-${claim.id}`}><Wallet className="h-4 w-4 mr-1" /> Payment Tracker</Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
          {/* Pagination footer (global list only) */}
          {!agentIdFilter && totalClaims > pageSize && (
            <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
              <div className="text-sm text-gray-600">
                Page <span className="font-semibold">{page}</span> of {Math.max(1, Math.ceil(totalClaims / pageSize))}
              </div>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1 || loading} onClick={() => setPage(p => Math.max(1, p - 1))} data-testid="claims-prev-page">Previous</Button>
                <Button size="sm" variant="outline" disabled={loading || page >= Math.ceil(totalClaims / pageSize)} onClick={() => setPage(p => p + 1)} data-testid="claims-next-page">Next</Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Sub-components */}
      <ClaimDetailsSheet
        open={detailsOpen} onOpenChange={setDetailsOpen} claim={selectedClaim}
        isPIC={isPIC} isAdmin={isAdmin} isAccount={isAccount} canApprove={canApprove}
        onStartReview={handleStartReview} onApprove={handleApprove} onProcessPayment={handleProcessPayment}
        onRequestRevision={() => setRevisionDialogOpen(true)} onReject={handleReject} onLoadAuditTrail={loadAuditTrail}
      />
      <PaymentTrackerDialog
        open={trackerOpen} onOpenChange={setTrackerOpen} data={trackerData} loading={trackerLoading}
        onMarkDeveloperFunded={handleMarkDeveloperFunded} onMarkRecipientPaid={handleMarkRecipientPaid} onDownloadReceipt={handleDownloadReceipt}
        devFundOpen={devFundOpen} setDevFundOpen={setDevFundOpen} devFundForm={devFundForm} setDevFundForm={setDevFundForm}
        payRecipientOpen={payRecipientOpen} setPayRecipientOpen={setPayRecipientOpen} payForm={payForm} setPayForm={setPayForm}
        payLoading={payLoading}
      />
      <RevisionDialog open={revisionDialogOpen} onOpenChange={setRevisionDialogOpen} comment={revisionComment} setComment={setRevisionComment} fieldComments={fieldComments} setFieldComments={setFieldComments} onSubmit={handleRequestRevision} />
      <AuditTrailDialog open={auditTrailOpen} onOpenChange={setAuditTrailOpen} data={auditData} />
    </div>
  );
};

export default AdminCommissionClaims;
